import React from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import Image from "next/image";
import Head from "next/head";
import * as Router from "next/router";

import firebase from "../../../firebase.client";
import * as u from "../../../utils";
import * as gtag from "../../../utils/gtag";
import * as h from "../../../utils/hooks";
import * as UserContext from "../../../components/UserContext";
import * as T from "../../../components/Typography";
import * as I from "../../../components/Icons";
import Link from "../../../components/Link";
import Button from "../../../components/Button";
import UpsellModal from "../../../components/UpsellModal";
import Input from "../../../components/Input";
import configClient from "../../../config.client";

const TOPICS = {
  Work: [
    "Entrepreneurs",
    "C Suite",
    "Corporate",
    "Freelance",
    "Small Business",
    "Leadership",
    "Gen Z",
    "Work",
  ],
  Style: ["Fashion", "Interiors", "Beauty", "Shopping", "Events"],
  Wellbeing: [
    "Health & Wellness",
    "Motherhood",
    "Relationships",
    "Books",
    "Podcast",
  ],
  Money: ["Investing", "Budgeting", "Raising Capital", "Debt", "Money"],
  "Diversity & Inclusion": ["Diversity & Inclusion"],
  "AllBright News": ["AllBright News"],
};

export default function EditIndex(props) {
  const userInfo = React.useContext(UserContext.Context);
  const { hasLoaded } = userInfo;
  const isLoggedIn = !!userInfo.id;
  const router = Router.useRouter();
  const [page] = Object.keys(router.query);
  const [value] = Object.values(router.query);
  const [search, setSearch] = React.useState("");
  const [modalType, setModalType] = React.useState(false);
  const pageArticles = props.articles.filter(byKey);
  const { pageTitle, pageInfo, pageImg } = getPageTitle(pageArticles);
  const [isFollowing, setIsFollowing] = React.useState();
  React.useEffect(onInit, [page]);
  React.useEffect(() => setSearch(value), [value]);

  if (!pageArticles.length) return empty();

  return (
    <S.Background>
      {getHead()}
      {hasLoaded && (
        <S.Container>
          <UpsellModal type={modalType} setType={setModalType} />
          {header()}
          <S.Articles>{pageArticles?.slice(1).map(getArticle)}</S.Articles>
        </S.Container>
      )}
    </S.Background>
  );

  function onInit() {
    if (!(page === "author" || page === "topic")) {
      if (!isLoggedIn) return setModalType(page);
      return setIsFollowing();
    }

    const isFollowingAuthor = userInfo.authors?.includes(value);
    const isFollowingTopic = userInfo.topics?.some((t) => value.startsWith(t));
    setIsFollowing(isFollowingAuthor || isFollowingTopic || false);
  }

  function header() {
    return (
      <S.Header>
        {getFeature()}
        {getIntro()}
      </S.Header>
    );

    function getFeature() {
      const [feature] = pageArticles;

      return (
        <SH.Feature onClick={() => onClickArticle("Feature")}>
          {feature.heroImage?.image?.url && (
            <Link href={`/edit/articles/${feature.url}`}>
              <SH.Image>
                <Image {...getImageProps(feature.heroImage.image)} />
              </SH.Image>
            </Link>
          )}
          <ArticleInfo article={feature} setModalType={setModalType} isLarge />
        </SH.Feature>
      );
    }

    function getIntro() {
      const btnProps = {
        text: isFollowing ? "Following" : "Follow",
        isFollowing,
        isSecondary: true,
        onClick: attemptToggleFollow,
      };

      return (
        <SH.Intro>
          <Link href="/edit">
            <SH.Logo>
              <TT.H5>The AllBright</TT.H5>
              <TT.H1S>
                Po<TT.Em>s</TT.Em>t
              </TT.H1S>
            </SH.Logo>
          </Link>
          <div>
            <SH.Flex>
              <TT.H2>{pageTitle}</TT.H2>
              {isFollowing !== undefined && <Button {...btnProps} />}
            </SH.Flex>
            <T.CM opacity={1}>{pageInfo}</T.CM>
          </div>
          {page === "search" && (
            <form onSubmit={onSearch}>
              <Input
                hint="Search EDIT... (enter to search)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </form>
          )}
          {page === "topic" && (
            <SH.Container>
              <TT.CMB>Related Topics</TT.CMB>
              {related()}
            </SH.Container>
          )}
          {isFollowing !== undefined && (
            <SH.Container popular>
              <TT.CMB>Popular from {pageTitle}</TT.CMB>
              {getPopular()}
            </SH.Container>
          )}
        </SH.Intro>
      );

      async function attemptToggleFollow() {
        if (await onToggleFollow()) return;
        setModalType("following");
      }

      async function onToggleFollow() {
        if (!isLoggedIn) return null;

        const db = firebase.firestore();
        const { arrayUnion, arrayRemove } = firebase.firestore.FieldValue;
        const userRef = db.collection("users").doc(userInfo.id);
        const authorRef = db
          .collection("authors")
          .doc(pageArticles[0].author.id);

        await db.runTransaction(async (t) => {
          const arrayFn = isFollowing ? arrayRemove : arrayUnion;
          switch (page) {
            case "author":
              t.update(authorRef, { users: arrayFn(userInfo.id) });
              t.update(userRef, { authors: arrayFn(value) });
              break;
            case "topic":
              t.update(userRef, { topics: arrayFn(value) });
              break;
            default:
              return;
          }
          setIsFollowing(!isFollowing);
        });

        const currUser = (await userRef.get()).data();
        userInfo.updateUserInfo(currUser);

        return true;
      }

      function onSearch(e) {
        e.preventDefault();
        if (search?.length) router.push(`/edit/articles?search=${search}`);
      }

      function related() {
        const category = Object.keys(TOPICS).find((cat) =>
          TOPICS[cat].includes(pageTitle)
        );
        const emptyTopics = TOPICS[category]?.filter(
          (cat) => !props.articles.some((a) => a.topic === cat)
        );

        return <SHR.List>{TOPICS[category]?.map(getRelatedTopic)}</SHR.List>;

        function getRelatedTopic(elm) {
          if ([...emptyTopics, pageTitle].includes(elm)) return null;

          return (
            <Link href={`/edit/articles?topic=${elm}`} key={`topic-${elm}`}>
              <SHR.Item>
                <T.CXS>{elm}</T.CXS>
              </SHR.Item>
            </Link>
          );
        }
      }

      function getPopular() {
        const byPopular = (a, b) => b.benchmark - a.benchmark;

        return (
          <SHP.List>
            {pageArticles
              .sort(byPopular)
              .filter((a) => a.benchmark)
              .slice(0, 5)
              .map(getHeadline)}
          </SHP.List>
        );

        function getHeadline(elm) {
          return (
            <Link href={`/edit/articles/${elm.url}`} key={elm.url}>
              <SHP.Item onClick={() => onClickArticle("Most popular")}>
                <TT.CM>{elm.title}</TT.CM>
              </SHP.Item>
            </Link>
          );
        }
      }
    }
  }

  function getArticle(elm) {
    if (!elm.heroImage) {
      console.log(elm.id);
      return null;
    }

    return (
      <SA.Card key={elm.url} onClick={() => onClickArticle("More")}>
        {elm.heroImage?.image?.url && (
          <Link href={`/edit/articles/${elm.url}`}>
            <SA.Image>
              <Image {...getImageProps(elm.heroImage.image)} />
            </SA.Image>
          </Link>
        )}
        <ArticleInfo article={elm} setModalType={setModalType} />
      </SA.Card>
    );
  }

  function byKey(elm) {
    const followsAuthor = userInfo.authors?.includes(elm.author.id);
    const followsTopic = userInfo.topics?.some((t) => elm.topic.startsWith(t));
    const sharesGoal = userInfo.goals?.some((goal) =>
      elm.goals?.includes(goal)
    );
    const hasNotRead = !elm.readBy?.includes(userInfo.id);

    switch (page) {
      case "topic":
        return elm.topic.startsWith(value);
      case "author":
        return elm.author.id === value;
      case "saved":
        return userInfo.articles?.includes(elm.id);
      case "following":
        return followsAuthor || followsTopic;
      case "recommended":
        return sharesGoal && hasNotRead;
      case "search":
        return searchTitle(value.split(" "));
      default:
        return true;
    }

    function searchTitle(terms) {
      const title = elm.title.split(" ");
      const description = elm.description?.split(" ") || [];
      const author = elm.author.name.split(" ");
      const searchableWords = [...title, ...description, ...author];

      return terms.every((term) =>
        searchableWords.some((word) =>
          word.toLowerCase().startsWith(term.toLowerCase())
        )
      );
    }
  }

  function getPageTitle() {
    switch (page) {
      case "topic":
        return topic();
      case "author":
        return author();
      case "saved":
        return { pageTitle: "Your saved articles" };
      case "following":
        return { pageTitle: "Articles you care about" };
      case "recommended":
        return {
          pageTitle: "Articles we think you'll enjoy based on your goals",
        };
      case "search":
        return {
          pageTitle:
            pageArticles.length > 0
              ? `Search results for "${value}"`
              : `There were no search results for "${value}"`,
        };
      default:
        return { pageTitle: "There are no articles here" };
    }

    function topic() {
      const byTopic = (article) => article.topic.startsWith(value);
      return {
        pageTitle: pageArticles?.find(byTopic)?.topic || "Author not found",
      };
    }

    function author() {
      const byAuthor = (article) => article.author.id === value;
      const author = pageArticles?.find(byAuthor)?.author;
      return {
        pageTitle: author?.name || "Author not found",
        pageInfo: author.bio,
        pageImg: author.imageSrc,
      };
    }
  }

  function empty() {
    const isSearch = page === "search";

    return (
      <S.Background>
        <Head>
          <title>{`${u.trimStr(pageTitle, 40)} | Post | AllBright`}</title>
        </Head>
        <S.Container isEmpty>
          <UpsellModal type={modalType} setType={setModalType} />
          <Link href="/edit">
            <SH.Logo>
              <TT.H5>The AllBright</TT.H5>
              <TT.H1S>
                Po<TT.Em>s</TT.Em>t
              </TT.H1S>
            </SH.Logo>
          </Link>
          <TT.H2>{pageTitle}</TT.H2>
          {isSearch && (
            <form onSubmit={onSearch}>
              <Input
                hint="Search AllBright Post... (enter to search)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </form>
          )}
          <TT.H2>
            <T.A href="/edit">Discover more from The AllBright Post</T.A>
          </TT.H2>
        </S.Container>
      </S.Background>
    );

    function onSearch(e) {
      e.preventDefault();
      if (search?.length) router.push(`/edit/articles?search=${search}`);
    }
  }

  function getHead() {
    const title = u.trimStr(pageTitle, 50) + " | Post | AllBright";
    const info = {
      title,
      description:
        pageInfo || `Read articles from the AllBright Post: ${pageTitle}.`,
      imgW: 500,
      imgH: 500,
      imgUrl: pageImg || `${configClient.ALLBRIGHT_URL}/images/edit.webp`,
      imgAlt: `Read articles from AllBright Post: ${pageTitle}.`,
      url: configClient.ALLBRIGHT_URL + router.asPath,
    };

    const metaTags = u.generateMetaTags(info);

    return (
      <Head>
        <title>{title}</title>
        {metaTags.map((elm) => (
          <meta {...elm} key={elm.name || elm.itemprop || elm.property} />
        ))}
      </Head>
    );
  }

  function getImageProps(image) {
    return {
      src: image.url,
      alt: image.title,
      layout: "fill",
      objectFit: "cover",
    };
  }
}

function onClickArticle(src) {
  gtag.event({
    name: gtag.GA_EVENT.EDIT_ENGAGEMENT,
    category: "EDIT",
    label: "Click From article",
    value: src,
  });
}

function ArticleInfo(props) {
  const [isSaved, onToggleSave] = h.useEditToggle(
    "articles",
    props.article.sys.id
  );
  const isLong = props.article.title.length > 100;
  const date = props.article.firstPublished
    ? dayjs.unix(props.article.firstPublished).format("DD MMMM YYYY")
    : "";
  const timeToRead = Math.round(props.article.wordCount / 200);
  // const commentCount = 26;

  return (
    <SI.Article>
      <div>
        <SI.Info>
          <Link href={`/edit/articles?topic=${props.article.topic}`}>
            <TT.CSB>{props.article.topic}</TT.CSB>
          </Link>
          <TT.CS>{date}</TT.CS>
        </SI.Info>
        <Link href={`/edit/articles/${props.article.url}`}>
          <TT.CL isLarge={props.isLarge} isLong={isLong}>
            {props.article.title}
          </TT.CL>
        </Link>
        <SI.Info>
          <TT.CS>
            By{" "}
            <Link href={`/edit/articles?author=${props.article.author.id}`}>
              <TT.Author>{props.article.author.name}</TT.Author>
            </Link>
          </TT.CS>
          {props.article.wordCount && <TT.CS>{timeToRead} min read</TT.CS>}
        </SI.Info>
      </div>
      <div>
        <SI.Info>
          {/* <Link href={`/edit/articles/${props.article.url}#comments`}>
            <SI.InfoWithIcon>
              <I.Comment stroke={u.colours.charcoal} />
              <TT.CS>{commentCount}</TT.CS>
            </SI.InfoWithIcon>
          </Link> */}
          <SI.InfoWithIcon onClick={attemptToggleSave}>
            <I.Bookmark fill={isSaved ? u.colours.charcoal : "transparent"} />
            <TT.CS>{isSaved ? "Saved" : "Save"}</TT.CS>
          </SI.InfoWithIcon>
        </SI.Info>
      </div>
    </SI.Article>
  );

  async function attemptToggleSave() {
    if (await onToggleSave()) return;
    props.setModalType("saved");
  }
}

export async function getStaticProps() {
  const axios = require("axios");
  const firebase = require("../../../firebase.admin");
  const db = firebase.firestore();
  const fbArticleData = (await db.collection("articles").get()).docs.map((d) =>
    d.data()
  );
  const fbAuthorData = (await db.collection("authors").get()).docs.map((d) =>
    d.data()
  );

  const config = require("../../../config");
  const byLatest = (a, b) => b.firstPublished - a.firstPublished;
  const articlesCount = (
    await axios.post(config.CONTENTFUL_EDIT, getGraphCount())
  ).data.data.articleCollection.total;
  let data = [];
  for (let skip = 0; skip < articlesCount; skip += 100) {
    data = data.concat(
      (await axios.post(config.CONTENTFUL_EDIT, getGraphQuery(skip))).data.data
        .articleCollection.items
    );
  }
  const articles = data.map(addInfo).sort(byLatest);

  return { props: { articles }, revalidate: 3600 };

  function addInfo(article) {
    const fbArticle = fbArticleData.find((a) => a.id === article.sys.id);
    const fbAuthor =
      fbAuthorData.find((a) => a.email === article.author) ||
      fbAuthorData.find((a) => a.email === "digital@allbright.co");
    const firstPublished =
      fbArticle?.firstPublished ||
      dayjs(article.sys.firstPublishedAt).unix() ||
      null;

    return { ...article, ...fbArticle, author: fbAuthor, firstPublished };
  }

  function getGraphCount() {
    return { query: `{articleCollection {total}}` };
  }

  function getGraphQuery(skip) {
    return {
      query: `{
        articleCollection(limit: 100, skip: ${skip}) {
          items {
            author
            goals
            title
            topic
            url
            sys {id firstPublishedAt}
            heroImage {
              image {title url}
            }
          }
        }
      }`,
    };
  }
}

const S = {};

S.Background = styled.div`
  background: white;
  min-height: calc(100vh - 60px);
  width: 100vw;
  @media ${u.device.tablet} {
    min-height: calc(100vh - 84px);
  }
`;

S.Container = styled.div`
  margin: 0 auto;
  min-height: calc(100vh - 60px);
  max-width: ${(p) => (p.isEmpty ? "400px" : "950px")};
  padding: 60px 16px;
  position: relative;
  @media ${u.device.tablet} {
    min-height: calc(100vh - 84px);
    padding: 60px 40px;
  }
`;

S.Image = styled.div`
  cursor: pointer;
  position: relative;
`;

S.Header = styled.section`
  display: flex;
  flex-direction: column-reverse;
  @media (min-width: 720px) {
    display: grid;
    gap: 40px;
    grid-template-columns: auto 310px;
  }
  @media ${u.device.tablet} {
    gap: 80px;
  }
`;

S.Articles = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 40px;
  @media ${u.device.tablet} {
    gap: 40px;
  }
`;

const SH = {};

SH.Feature = styled.article``;

SH.Image = styled(S.Image)`
  height: 0;
  margin-left: -16px;
  padding-top: 100%;
  position: relative;
  width: 100vw;
  @media (min-width: 720px) {
    margin: 0;
    width: 100%;
  }
`;

SH.Intro = styled.section`
  margin-bottom: 20px;
  @media (min-width: 720px) {
    margin: 0;
  }
`;

SH.Logo = styled.article`
  cursor: pointer;
  margin-bottom: 60px;
  text-align: center;
  text-transform: uppercase;
`;

SH.Flex = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-evenly;
  margin: 20px 0;
`;

SH.Container = styled.article`
  margin-top: 40px;
  text-align: center;
  ${(p) => p.popular && "display: none;"}
  @media (min-width: 720px) {
    border: 1px solid ${u.colours.shellOverlay};
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-bottom: 40px;
    padding: 20px;
    text-align: initial;
  }
`;

const SHR = {};

SHR.List = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  list-style: none;
  overflow: hidden;
  @media (min-width: 720px) {
    justify-content: initial;
  }
`;

SHR.Item = styled.li`
  border: 1px solid ${u.colours.charcoalFaded};
  border-radius: 100px;
  cursor: pointer;
  margin-right: 10px;
  margin-top: 10px;
  padding: 10.5px 15px;
`;

const SHP = {};

SHP.List = styled.ol`
  font-weight: bold;
  padding-left: 20px;
`;

SHP.Item = styled.li`
  margin: 20px 0;
`;

const SA = {};

SA.Card = styled.article`
  @media ${u.device.mobile} {
    display: flex;
    > * + * {
      margin-left: 20px;
    }
  }
`;

SA.Image = styled(S.Image)`
  height: 0;
  padding-top: 100%;
  width: 100%;
  @media ${u.device.mobile} {
    height: 194px;
    min-width: 194px;
    max-width: 194px;
    padding: 0;
    width: initial;
  }
  @media (min-width: 720px) {
    height: 252px;
    min-width: 252px;
    max-width: 252px;
  }
`;

const SI = {};

SI.Article = styled.div`
  cursor: default;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

SI.Info = styled.div`
  align-items: center;
  display: flex;
  margin: 20px 0 10px;
`;

SI.InfoWithIcon = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  > * + * {
    margin-left: 10px;
  }
  margin-right: 25px;
`;

const TT = {};

TT.H1S = styled(T.H1Rox)`
  font-size: 9.4rem;
  font-weight: 300;
  line-height: 8.8rem;
  letter-spacing: 2rem;
`;

TT.H2 = styled(T.H2)`
  font-size: 2rem;
  line-height: 2.5rem;
  margin: 20px;
  text-transform: initial;
  text-align: center;
`;

TT.H5 = styled(T.H5)`
  letter-spacing: 0.3rem;
`;

TT.CL = styled(T.CL)`
  color: ${u.colours.charcoal};
  cursor: pointer;
  margin: 10px 0;
  ${(p) =>
    p.isLarge &&
    `
    font-size: 2.4rem;
    line-height: 3.6rem;
  `}
  ${(p) =>
    p.isLong ? "@media (min-width: 900px)" : "@media (min-width: 720px)"} {
    font-size: 2.4rem;
    line-height: 3.6rem;
  }
`;

TT.CM = styled(T.CM)`
  cursor: pointer;
  opacity: 1;
`;

TT.CMB = styled(TT.CM)`
  font-weight: bold;
`;

TT.CS = styled(T.CS)`
  margin-right: 10px;
  opacity: 1;
  text-transform: uppercase;
  @media ${u.device.desktop} {
    margin-right: 20px;
  }
`;

TT.CSB = styled(TT.CS)`
  cursor: pointer;
  font-weight: bold;
  letter-spacing: 0.1rem;
`;

TT.Author = styled.span`
  cursor: pointer;
`;

TT.Em = styled.em`
  margin-left: -1rem;
`;
